
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { serviceApi, serviceCategoryApi, serviceContentApi, assetUrl } from '../services/api';
import { Link, useSearchParams } from 'react-router-dom';

type Service = {
  id: string;
  title: string;
  category: string;
  description: string;
  tags: string[];
  features: any[];
  color: string;
  gradient: string;
  heading_color?: string;
  para_color?: string;
  gradient_start?: string;
  gradient_end?: string;
  slug?: string;
  image?: string;
};

type ServiceRow = {
  id: number | string;
  title?: string | null;
  subtitle?: string | null;
  category?: string | null;
  description?: string | null;
  features?: any[];
  tags?: string[];
  color?: string | null;
  heading_color?: string | null;
  para_color?: string | null;
  gradient?: string | null;
  gradient_start?: string | null;
  gradient_end?: string | null;
  is_active?: boolean | null;
  detail_page_slug?: string | null;
  image?: string | null;
  image_url?: string | null;
  featured_image?: string | null;
};

type ServicesHeroSettings = {
  badge_text: string;
  stat1_label: string;
  stat1_value: string;
  stat2_label: string;
  stat2_value: string;
  stat3_label: string;
  stat3_value: string;
};

type ServicesCtaSettings = {
  heading: string;
  description: string;
  primary_text: string;
  primary_action: 'modal' | 'link';
  primary_link: string;
  secondary_text: string;
  secondary_action: 'modal' | 'link';
  secondary_link: string;
};


// Service Showcase Component - Inspired by updatedservices
const ServiceShowcase: React.FC<{ service: Service; index: number }> = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="relative group"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <div className="relative overflow-hidden rounded-[3rem] bg-black border border-white/10">
        {/* Image Background */}
        <div className="relative h-[400px] md:h-[500px] overflow-hidden">
          <img 
            src={service.image ? assetUrl(service.image) : `https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1600&q=80`}
            alt={service.title}
            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          
        </div>
        
        {/* Content Section */}
        <div className="p-8 md:p-12">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight break-words">
                {service.title}
              </h3>
              
              <div className="w-20 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mb-6 shadow-[0_0_20px_rgba(249,115,22,0.5)]" />
            </div>
          </div>
          
          <p className="text-lg text-gray-300 leading-relaxed mb-8 line-clamp-2 min-h-[3.5rem]">
            {service.description}
          </p>
          
          {/* Features Grid */}
          {service.features && service.features.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {service.features.slice(0, 4).map((feature, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-sm text-gray-400">
                    {typeof feature === 'string' ? feature : feature.title || feature.text}
                  </span>
                </div>
              ))}
            </div>
          )}
          
          {/* Tags */}
          {service.tags && service.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {service.tags.slice(0, 3).map((tag, i) => (
                <span 
                  key={i} 
                  className="px-3 py-1 text-xs font-mono uppercase tracking-wider border border-white/10 bg-white/5 text-gray-400 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          {/* CTA Button */}
          <div className="flex items-center justify-between">
            <Link 
              to={`/service/${service.slug || ''}`}
              className="flex items-center gap-3 text-orongne-400 font-bold uppercase tracking-wider text-sm group"
            >
              Explore Service →
            </Link>
            
                      </div>
        </div>
      </div>
    </motion.div>
  );
};

export const ServicesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState<Service[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [categoryDescriptions, setCategoryDescriptions] = useState<Record<string, string>>({});
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [sectionHeading, setSectionHeading] = useState<string>('Our Services');
  const [sectionDescription, setSectionDescription] = useState<string>('');
  const [heroSettings, setHeroSettings] = useState<ServicesHeroSettings>({
    badge_text: 'Premium Solutions',
    stat1_label: 'Projects',
    stat1_value: '50+',
    stat2_label: 'Years',
    stat2_value: '10+',
    stat3_label: 'Success',
    stat3_value: '100%',
  });
  const [ctaSettings, setCtaSettings] = useState<ServicesCtaSettings>({
    heading: 'Ready to Start Your Project?',
    description: "Let's create something amazing together. Get in touch with our team to discuss your requirements.",
    primary_text: 'Start Project',
    primary_action: 'modal',
    primary_link: '',
    secondary_text: 'View Portfolio',
    secondary_action: 'link',
    secondary_link: '/portfolio',
  });

  const categories = useMemo(() => {
    const values = categoryOptions.length > 0
      ? categoryOptions
      : Array.from(new Set(services.map((service) => service.category).filter(Boolean)));
    return ['All', ...values];
  }, [categoryOptions, services]);

  const filteredServices = useMemo(() => {
    if (selectedCategory === 'All') return services;
    return services.filter((service) => service.category === selectedCategory);
  }, [selectedCategory, services]);

  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    setSelectedCategory(categoryFromUrl && categoryFromUrl.trim() ? categoryFromUrl.trim() : 'All');
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [res, categoryRes] = await Promise.all([
          serviceApi.getAll(),
          serviceCategoryApi.getAll(true).catch(() => null),
        ]);
        if (res?.success && Array.isArray(res.data)) {
          const colors = ['cyan', 'pink', 'purple', 'amber', 'emerald'];
          const gradients = [
            'from-cyan-400 to-blue-500',
            'from-pink-500 to-rose-500',
            'from-purple-500 to-indigo-500',
            'from-amber-400 to-amber-500',
            'from-emerald-400 to-teal-500',
          ];
          const activeRows = (res.data as ServiceRow[]).filter((row) => row.is_active !== false);
          const mapped = activeRows.map((row: ServiceRow, idx: number) => {
            const tags = Array.isArray(row.tags) ? row.tags : [];
            const features = Array.isArray(row.features) ? row.features : [];
            const base = String(row.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'service';
            const slug = String(row.detail_page_slug || `${base}-${row.id}`);
            return {
              id: String(row.id).padStart(2, '0'),
              title: String(row.title || ''),
              category: String(row.category || row.subtitle || 'General'),
              description: String(row.description || ''),
              tags: tags,
              features: features,
              color: (row.color || colors[idx % colors.length]) as string,
              gradient: (row.gradient || gradients[idx % gradients.length]) as string,
              heading_color: row.heading_color || undefined,
              para_color: row.para_color || undefined,
              gradient_start: row.gradient_start || undefined,
              gradient_end: row.gradient_end || undefined,
              image: row.featured_image || row.image_url || row.image || undefined,
              slug,
            };
          });
          setServices(mapped);
        }
        if (categoryRes?.success && Array.isArray(categoryRes.data)) {
          setCategoryOptions(
            categoryRes.data
              .map((category: { name?: string | null }) => String(category.name || '').trim())
              .filter(Boolean)
          );
          // Store category descriptions
          const descriptions: Record<string, string> = {};
          categoryRes.data.forEach((category: { name?: string | null; description?: string | null }) => {
            if (category.name) {
              descriptions[category.name] = String(category.description || '');
            }
          });
          setCategoryDescriptions(descriptions);
        }
      } catch (e) {
        console.error('Error fetching services:', e);
      }

      try {
        const h = await serviceContentApi.get();
        if (h?.success && h.data) {
          setSectionHeading(String(h.data.heading || 'Our Services'));
          setSectionDescription(String(h.data.description || ''));
          setHeroSettings({
            badge_text: String(h.data.badge_text || 'Premium Solutions'),
            stat1_label: String(h.data.stat1_label || 'Projects'),
            stat1_value: String(h.data.stat1_value || '50+'),
            stat2_label: String(h.data.stat2_label || 'Years'),
            stat2_value: String(h.data.stat2_value || '10+'),
            stat3_label: String(h.data.stat3_label || 'Success'),
            stat3_value: String(h.data.stat3_value || '100%'),
          });
          setCtaSettings({
            heading: String(h.data.cta_heading || 'Ready to Start Your Project?'),
            description: String(h.data.cta_description || "Let's create something amazing together. Get in touch with our team to discuss your requirements."),
            primary_text: String(h.data.button_text || 'Start Project'),
            primary_action: h.data.button_action === 'link' ? 'link' : 'modal',
            primary_link: String(h.data.button_link || ''),
            secondary_text: String(h.data.secondary_button_text || 'View Portfolio'),
            secondary_action: h.data.secondary_button_action === 'modal' ? 'modal' : 'link',
            secondary_link: String(h.data.secondary_button_link || '/portfolio'),
          });
        }
      } catch (e) {
        console.error('Error fetching services content:', e);
      }
    };
    fetchData();
  }, []);

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    if (category === 'All') {
      setSearchParams({});
      return;
    }
    setSearchParams({ category });
  };

  const getButtonHref = (action: 'modal' | 'link', link: string, fallback: string) => {
    if (action === 'modal') return '/contact';
    return link || fallback;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <motion.div 
          animate={{ 
            x: [0, 100, 0],
            y: [0, -100, 0]
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-amber-900/10 blur-[120px] rounded-full"
        />
        <motion.div 
          animate={{ 
            x: [0, -100, 0],
            y: [0, 100, 0]
          }}
          transition={{ 
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] bg-amber-900/10 blur-[120px] rounded-full"
        />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100px_100px]"></div>
      </div>

      {/* Hero Section */}
      <section className="relative z-10 min-h-screen flex items-center justify-center px-6 md:px-12 pt-24">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-amber-500/10 border border-amber-500/30 rounded-full">
              <span className="text-amber-400 font-bold uppercase tracking-wider text-sm">
                {heroSettings.badge_text}
              </span>
            </div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-6xl md:text-8xl lg:text-9xl font-black leading-tight mb-8"
          >
            {sectionHeading.split(' ').map((word, i) => (
              <span key={i} className={i === 1 ? "text-orongne-400 block" : "block text-white"}>
                {word}
              </span>
            ))}
          </motion.h1>
          
          {sectionDescription && (
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-3xl mx-auto"
            >
              <p className="text-xl md:text-2xl text-gray-400 leading-relaxed">
                {sectionDescription}
              </p>
            </motion.div>
          )}
          
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-12"
          >
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="text-4xl font-black text-orongne-400">{heroSettings.stat1_value}</div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">{heroSettings.stat1_label}</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-black text-orongne-400">{heroSettings.stat2_value}</div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">{heroSettings.stat2_label}</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-black text-orongne-400">{heroSettings.stat3_value}</div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">{heroSettings.stat3_label}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Showcase */}
      <section className="relative z-10 py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 flex flex-wrap justify-center gap-3">
            {categories.map((category) => {
              const isActive = selectedCategory === category;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => handleCategoryClick(category)}
                  className={`rounded-full border px-5 py-3 text-xs font-bold uppercase tracking-[0.25em] transition-colors ${
                    isActive
                      ? 'border-amber-500 bg-amber-500 text-white'
                      : 'border-white/10 bg-white/5 text-gray-300 hover:border-amber-500/40 hover:text-white'
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>

          {/* Category Heading and Description */}
          <AnimatePresence mode="wait">
            {selectedCategory !== 'All' && (
              <motion.div
                key={selectedCategory}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-12"
              >
                <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                  {selectedCategory}
                </h2>
                {categoryDescriptions[selectedCategory] && (
                  <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                    {categoryDescriptions[selectedCategory]}
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <AnimatePresence mode="popLayout">
              {filteredServices.map((service, index) => (
                <ServiceShowcase key={service.id} service={service} index={index} />
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-[3rem] bg-gradient-to-r from-[#FFBF00] to-[#FFD700] p-12 md:p-20"
          >
            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                {ctaSettings.heading}
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                {ctaSettings.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to={getButtonHref(ctaSettings.primary_action, ctaSettings.primary_link, '/contact')}
                  className="px-8 py-4 bg-white text-amber-600 font-bold rounded-full hover:scale-105 transition-transform"
                >
                  {ctaSettings.primary_text}
                </Link>
                <Link
                  to={getButtonHref(ctaSettings.secondary_action, ctaSettings.secondary_link, '/portfolio')}
                  className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-full hover:bg-white/10 transition-colors"
                >
                  {ctaSettings.secondary_text}
                </Link>
              </div>
            </div>
            
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='7' cy='7' r='7'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}></div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
